import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import useApiService from '../../../services/api'
import { useMobileSearch } from '../../../components/MobileSearch/hooks/useMobileSearch'
import {
  getMobileSearchTypeOptions,
  isFbSearchType,
} from '../../../components/MobileSearch/hooks/getMobileSearchTypeOptions'
import type { MobileSearchFilters as MobileSearchFiltersType } from '../../../components/MobileSearch/hooks/useMobileSearch'
import { MobileSearchDropdown } from '../../../components/MobileSearch/MobileSearchDropdown'
import { MobileSearchInput } from '../../../components/MobileSearch/MobileSearchInput'
import { MobileSearchResults } from '../../../components/MobileSearch/MobileSearchResults'
import { MobileSearchFilters } from '../../../components/MobileSearch/MobileSearchFilters'
import { MobileSearchSort } from '../../../components/MobileSearch/MobileSearchSort'
import { MobileSearchAppliedFilters } from '../../../components/MobileSearch/MobileSearchAppliedFilters'
import { BackButton } from '../../../components/MobileSearch/MobileSearch.styles'
import {
  ContactCompanySearchResult,
  DoorSearchResult,
} from '../../../models/companies'
import { SearchType } from 'components/MobileSearch/hooks/useMobileSearchAPI'
import { useMountEffect } from '../../../hooks/useMountEffect'

interface SearchPageProps {
  hideBack?: boolean
  onClick?: (company: ContactCompanySearchResult | DoorSearchResult) => void
  options?: { value: SearchType; label: string }[]
  initialQuery?: string
  selectedItemId?: string | number
  /**
   * Optional client-side predicate to narrow which results are shown. Used by
   * the mobile contact-create company step to show only results it can resolve
   * to a contact_company (e.g. restaurants + custom companies). Default: show
   * all. (Filters the loaded page client-side; the server count is unchanged.)
   */
  resultFilter?: (
    result: ContactCompanySearchResult | DoorSearchResult
  ) => boolean
}

export default function SearchPage({
  hideBack = false,
  onClick,
  options,
  initialQuery,
  selectedItemId,
  resultFilter,
}: SearchPageProps) {
  const navigate = useNavigate()
  const api = useApiService()
  const { data: categoriesData } = api.useGetActiveCompanyCategoriesAndTypes()
  const {
    state,
    setSearchType,
    setQuery,
    setFilters,
    setSort,
    removeFilter,
    clearSearch,
    loadMore,
  } = useMobileSearch()

  useMountEffect(() => {
    if (initialQuery) setQuery(initialQuery)
  })

  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isSortOpen, setIsSortOpen] = useState(false)

  // Hide First Bite search types when the active filter (e.g. grocery stores)
  // has no FB data. Callers may still pass explicit `options` to override.
  const searchTypeOptions = useMemo(
    () => options ?? getMobileSearchTypeOptions(categoriesData, state.filters),
    [options, categoriesData, state.filters]
  )

  // When applying a filter that removes FB data, drop any FB search type so the
  // user isn't stranded on an empty First Bite search (event-handler, no effect).
  const handleApplyFilters = (filters: MobileSearchFiltersType) => {
    setFilters(filters)
    const nextOptions =
      options ?? getMobileSearchTypeOptions(categoriesData, filters)
    if (
      isFbSearchType(state.searchType) &&
      !nextOptions.some((opt) => opt.value === state.searchType)
    ) {
      setSearchType('custom_company')
    }
  }

  const handleBack = () => {
    navigate(-1)
  }

  const handleFilterClick = () => {
    setIsSortOpen(false)
    setIsFilterOpen(true)
  }

  const handleSortClick = () => {
    setIsFilterOpen(false)
    setIsSortOpen(true)
  }

  const visibleResults = resultFilter
    ? state.results.filter(resultFilter)
    : state.results

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">
      {/* Back Button */}
      {!hideBack && (
        <BackButton onClick={handleBack}>
          <ChevronLeft size={20} />
          <span>Back</span>
        </BackButton>
      )}

      {/* Search Controls */}
      <div className="px-2 pt-3 pb-1.5 bg-white">
        <MobileSearchDropdown
          value={state.searchType}
          label={state.searchTypeLabel}
          onChange={setSearchType}
          options={searchTypeOptions}
        />
        <MobileSearchInput
          value={state.query}
          onChange={setQuery}
          onClear={clearSearch}
          onFilterClick={handleFilterClick}
          onSortClick={handleSortClick}
          currentSort={state.sort}
          isSortOpen={isSortOpen}
        />
        <MobileSearchAppliedFilters
          filters={state.filters}
          onRemoveFilter={removeFilter}
        />
      </div>

      {/* Results */}
      <MobileSearchResults
        results={visibleResults}
        total={state.total}
        query={state.query}
        searchType={state.searchType}
        isLoading={state.isLoading}
        isLoadingMore={state.isLoadingMore}
        hasMore={state.hasMore}
        error={state.error}
        onLoadMore={loadMore}
        isDebouncing={state.isDebouncing}
        onClick={onClick}
        selectedItemId={selectedItemId}
      />

      {/* Filter Panel - covers entire area between headers and footers */}
      <MobileSearchFilters
        isOpen={isFilterOpen}
        filters={state.filters}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleApplyFilters}
      />

      {/* Sort Panel - covers entire area between headers and footers */}
      <MobileSearchSort
        isOpen={isSortOpen}
        currentSort={state.sort}
        onClose={() => setIsSortOpen(false)}
        onSelect={setSort}
      />
    </div>
  )
}
